import { useState, useEffect } from 'react';

interface HeroProps {
  onGetQuote: () => void;
  onViewPortfolio: () => void;
}

export default function Hero({ onGetQuote }: HeroProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/c3f093db5b580ea1f48e3c5b121526de.jpg"
          alt="Home Remodeling"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6 flex items-center justify-between">
          <img 
            src="https://static.readdy.ai/image/839dec6a9613ad5b94c8c6c1844a10c6/3ee9a274e58c80cc0e503777418aeb7b.png" 
            alt="BestCall Restor" 
            className="h-8 sm:h-10 lg:h-12 w-auto"
          />
          <div className="hidden lg:flex items-center gap-8 xl:gap-12">
            <a onClick={() => scrollToSection('services')} className={`text-sm font-semibold cursor-pointer transition-colors ${isScrolled ? 'text-gray-900 hover:text-[#2563EB]' : 'text-white hover:text-[#2563EB]'}`}>Services</a>
            <a onClick={() => scrollToSection('portfolio')} className={`text-sm font-semibold cursor-pointer transition-colors ${isScrolled ? 'text-gray-900 hover:text-[#2563EB]' : 'text-white hover:text-[#2563EB]'}`}>Portfolio</a>
            <a onClick={() => scrollToSection('about')} className={`text-sm font-semibold cursor-pointer transition-colors ${isScrolled ? 'text-gray-900 hover:text-[#2563EB]' : 'text-white hover:text-[#2563EB]'}`}>About</a>
            <a onClick={() => scrollToSection('contact')} className={`text-sm font-semibold cursor-pointer transition-colors ${isScrolled ? 'text-gray-900 hover:text-[#2563EB]' : 'text-white hover:text-[#2563EB]'}`}>Contact</a>
          </div>
          <button 
            onClick={onGetQuote}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            Get Quote
          </button>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-4 sm:mb-6">
            <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold border border-white/20">
              Winter Garden's Trusted Remodeling Experts
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
            Transform Your Home<br />
            Into Your Dream Space
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-8 sm:mb-10 lg:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
            Professional remodeling services in Winter Garden, FL • 70+ completed projects • Fully insured
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 sm:mb-16 lg:mb-20 px-4">
            <button 
              onClick={onGetQuote}
              className="w-full sm:w-auto bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Start Your Project
            </button>
            <button 
              onClick={() => scrollToSection('portfolio')}
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 sm:px-10 py-3.5 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              View Portfolio
            </button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-12 px-4">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-3 rounded-full border border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-xl sm:text-2xl text-white"></i>
              </div>
              <div className="text-left">
                <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white whitespace-nowrap">3+ Years</div>
                <div className="text-xs sm:text-sm text-white/80 whitespace-nowrap">Experience</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-3 rounded-full border border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-hammer-line text-xl sm:text-2xl text-white"></i>
              </div>
              <div className="text-left">
                <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white whitespace-nowrap">70+ Projects</div>
                <div className="text-xs sm:text-sm text-white/80 whitespace-nowrap">Completed</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-3 rounded-full border border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-xl sm:text-2xl text-white"></i>
              </div>
              <div className="text-left">
                <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white whitespace-nowrap">Fully Insured</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
